import React from 'react';
import PropTypes from 'prop-types';

import { Badge } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faCircle, faDotCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  auroMetalSaurus,
  backgroundGrey5,
  borderGray,
  grape,
  greyStroke,
  positiveGreen,
  positiveGreenBackground,
  shadowGrape,
  squidInk,
  white,
} from 'lib/css/colors';

function ManufacturerPanel({ active, highlight, onActivate, headerLabel, headerExtras, children }) {
  const handleActivate = () => {
    if (!active) onActivate();
  };
  const inactiveStyle = highlight ? styles.mfrPanelHighlight : styles.mfrPanelInactive;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onFocus={handleActivate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          const tag = e.target?.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
          e.preventDefault();
          handleActivate();
        }
      }}
      className={css(styles.mfrPanel, active ? styles.mfrPanelActive : inactiveStyle)}
    >
      <div className={css(styles.mfrPanelHeader)}>
        <FontAwesomeIcon
          icon={active ? faDotCircle : faCircle}
          className={css(
            styles.mfrPanelRadio,
            active ? styles.mfrPanelRadioActive : styles.mfrPanelRadioInactive,
          )}
        />
        <span
          className={css(styles.mfrPanelHeaderLabel, active && styles.mfrPanelHeaderLabelActive)}
        >
          {headerLabel}
        </span>
        {active && (
          <Badge className={classNames(css(styles.mfrPanelSelectedBadge), 'ml-2')}>SELECTED</Badge>
        )}
        <div className={css(styles.mfrPanelHeaderSpacer)} />
        {headerExtras}
      </div>
      <div className={css(styles.mfrPanelBody)}>{children}</div>
    </div>
  );
}

ManufacturerPanel.propTypes = {
  active: PropTypes.bool.isRequired,
  highlight: PropTypes.bool,
  onActivate: PropTypes.func.isRequired,
  headerLabel: PropTypes.string.isRequired,
  headerExtras: PropTypes.node,
  children: PropTypes.node,
};

const styles = StyleSheet.create({
  mfrPanel: {
    border: `1px solid ${borderGray}`,
    borderRadius: 8,
    padding: '12px 14px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    outline: 'none',
    flex: '1 1 240px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  mfrPanelActive: {
    border: `2px solid ${grape}`,
    backgroundColor: white,
    boxShadow: `0 0 0 4px ${shadowGrape}`,
    padding: '11px 13px',
  },
  mfrPanelInactive: {
    backgroundColor: backgroundGrey5,
    opacity: 0.85,
    ':hover': {
      opacity: 1,
      borderColor: greyStroke,
    },
  },
  mfrPanelHighlight: {
    border: `1px solid ${positiveGreen}`,
    backgroundColor: positiveGreenBackground,
    ':hover': {
      borderColor: positiveGreen,
    },
  },
  mfrPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  mfrPanelRadio: {
    fontSize: '0.95rem',
  },
  mfrPanelRadioActive: {
    color: grape,
  },
  mfrPanelRadioInactive: {
    color: auroMetalSaurus,
  },
  mfrPanelHeaderLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: auroMetalSaurus,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  mfrPanelHeaderLabelActive: {
    color: squidInk,
  },
  mfrPanelHeaderSpacer: {
    flex: 1,
  },
  mfrPanelSelectedBadge: {
    backgroundColor: grape,
    color: white,
    fontWeight: 700,
    fontSize: '0.65rem',
    padding: '3px 7px',
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  mfrPanelBody: {},
});

export default ManufacturerPanel;
